import { APIClient, AuthConfig, getClient } from '@360mediadirect/api-client'
import { Entitlement } from '../../../../common/interfaces/Entitlement'
import { LibraryIssue } from '../../../../common/interfaces/Issue'
import { Magazine } from '../../../../common/interfaces/Magazine'
import { SavedArticle } from '../type/article'
import { EntitlementArticle } from '../../../../common/interfaces/EntitlementArticle'

export const getClientApi = (authConfig: AuthConfig = {}): APIClient => {
  return getClient(authConfig, {
    baseURL: process.env.REACT_APP_API_CLIENT_BASE_URL
  })
}

const getAllEntitlements = async (client: APIClient) => {
  try {
    let allData: any = []
    let results: any
    let startKey = ''
    do {
      results = startKey
        ? await client.getEntitlements({ startKey })
        : await client.getEntitlements()
      if (results?.data?.entitlements?.length > 0) {
        allData = [...allData, ...results.data.entitlements]
      }
      startKey = results.data?.lastEvaluatedKey
        ? JSON.stringify(results.data.lastEvaluatedKey)
        : ''
    } while (results.data?.lastEvaluatedKey)

    return allData
  } catch (err) {
    console.warn('getAllEntitlements error: ', err)
    return err
  }
}

export const getIssues = async (client: APIClient): Promise<LibraryIssue[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      const entitlements: any = await getAllEntitlements(client)
      const ids: string[] = entitlements
        .map((e: any) => e.issueId || '')
        .filter((str: any) => !!str)

      if (ids && ids.length > 0) {
        client.findIssues(null, { ids }).then(
          (iResults) => {
            const combinedResults = (iResults.data.issues as LibraryIssue[])
              .map((i) => {
                const entitlement = entitlements.filter(
                  (e: Entitlement) => e.issueId === i.id
                )
                return {
                  ...i,
                  entitlementId: entitlement[0].id,
                  lastOpenedAt: entitlement[0].lastOpenedAt
                }
              })
              .filter((res) => !res.isDouble)
            resolve(combinedResults)
          },
          (err) => {
            console.warn('getIssues err: ', err)
            reject(err)
          }
        )
      }
    } catch (err) {
      console.warn('getAllEntitlements err: ', err)
      reject(err)
    }
  })
}

export const getIssueUrl = async (
  client: APIClient,
  id: string,
  article?: string | null,
  tag?: string | null
) => {
  try {
    const results = await client.getEntitlementUrl({
      id,
      article: article ? article : undefined,
      tag: tag ? tag : undefined
    })
    return results.data.signedUrl
  } catch (err: any) {
    console.warn('getEntitlementUrl err: ', err)
    return ''
  }
}

const getAllEntitlementArticles = async (
  client: APIClient,
  entitlementId: string
) => {
  try {
    let allData: any = []
    let results: any
    let startKey = ''
    do {
      results = startKey
        ? await client.getEntitlementArticles({ startKey, eId: entitlementId })
        : await client.getEntitlementArticles({ eId: entitlementId })
      if (results?.data?.articles?.length > 0) {
        allData = [...allData, ...results.data.articles]
      }
      startKey = results.data?.lastEvaluatedKey
        ? JSON.stringify(results.data.lastEvaluatedKey)
        : ''
    } while (results.data?.lastEvaluatedKey)

    return allData
  } catch (err) {
    console.warn('getAllEntitlementArticles error: ', err)
    return err
  }
}

export const getSavedArticles = async (
  client: APIClient,
  issues: LibraryIssue[]
): Promise<SavedArticle[]> => {
  try {
    let savedArticles: SavedArticle[] = []
    await Promise.all(
      issues.map(async (issue) => {
        const articles: any = await getAllEntitlementArticles(
          client,
          issue.entitlementId
        )

        if (articles.length > 0) {
          savedArticles.push({
            coverImageUrl: issue.coverImageUrl,
            entitlementId: issue.entitlementId,
            magTitle: issue.magTitle,
            issueDate: issue.issueDate,
            savedArticles: articles.sort(
              (a: any, b: any) => b.updatedAt - a.updatedAt
            )
          })
        }
      })
    )
    return savedArticles
  } catch (err: any) {
    console.warn('getEntitlementUrl err: ', err)
    return []
  }
}

export const deleteSavedArticles = async (
  client: APIClient,
  articles: EntitlementArticle[]
): Promise<boolean> => {
  try {
    for (const article of articles) {
      await client.deleteEntitlementArticle({
        eId: article.entitlementId,
        aId: article.id
      })
    }
    return true
  } catch (err: any) {
    console.warn('deleteSavedArticles err: ', err)
    return false
  }
}

export const postLog = async (client: APIClient, data: any) => {
  try {
    await client.clientLog(null, data)
  } catch (err: any) {
    console.warn('postLog err: ', err)
  }
}

export const getMagazines = async (
  client: APIClient,
  ids: string
): Promise<Magazine[]> => {
  return new Promise(async (resolve, reject) => {
    try {
      client.getMagazines({ ids }).then((results) => {
        resolve(results.data.magazines)
      })
    } catch (err) {
      console.warn('getMagazines err: ', err)
      reject(err)
    }
  })
}
